import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { JackpotListRow } from '@/components/delegate/JackpotListRow';
import { JackpotWizard } from '@/components/delegate/JackpotWizard';
import {
  useJackpotsList,
  useToggleJackpotPublish,
  useDeleteJackpot,
  useUserOrganizations,
} from '@/api/extensions/useJackpotOps';

const PAGE_SIZE = 12;

export default function DelegateJackpotsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [publishedFilter, setPublishedFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const isCreating = searchParams.get('create') === 'true';

  const filters = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      is_published:
        publishedFilter === 'all'
          ? undefined
          : publishedFilter === 'published',
    }),
    [page, search, publishedFilter],
  );

  const { data, isLoading } = useJackpotsList(filters);
  const { data: orgs } = useUserOrganizations();
  const togglePublish = useToggleJackpotPublish();
  const deleteJackpot = useDeleteJackpot();

  const jackpots = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const orgMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of orgs ?? []) {
      map[org.id] = org.name;
    }
    return map;
  }, [orgs]);

  const openCreate = () => setSearchParams({ create: 'true' });
  const closeWizard = () => setSearchParams({});

  const handleEdit = (id: string) => navigate(`/delegate/jackpots/${id}/edit`);
  const handleTogglePublish = (id: string) => togglePublish.mutate(id);

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    await deleteJackpot.mutateAsync(deleteId);
    setDeleteId(null);
  };

  if (isCreating) {
    return (
      <div className="space-y-4">
        <JackpotWizard onClose={closeWizard} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Jackpots</h1>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> Create Jackpot
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search jackpots..."
            className="pl-8"
          />
        </div>
        <Select
          value={publishedFilter}
          onValueChange={(v) => {
            setPublishedFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : jackpots.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="No jackpots found"
          description={
            search || publishedFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Create your first jackpot to get started.'
          }
          action={
            !search && publishedFilter === 'all'
              ? { label: 'Create Jackpot', onClick: openCreate }
              : undefined
          }
        />
      ) : (
        <>
          <div className="space-y-2">
            {jackpots.map((j: any) => (
              <JackpotListRow
                key={j.id}
                jackpot={j}
                orgName={orgMap[j.organization_id]}
                onEdit={handleEdit}
                onTogglePublish={handleTogglePublish}
                onDelete={setDeleteId}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Jackpot</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this jackpot? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteJackpot.isPending}
            >
              {deleteJackpot.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
